import { useMutation, useQueryClient } from "@tanstack/react-query";
import * as React from "react";

import { approveDeal, denyDeal, forceBypassDeal, type Deal } from "@/api/client";
import { useCurrentUser } from "@/auth/useCurrentUser";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { dealToasts, useToast } from "@/components/ui/toast";
import { FormError } from "@/components/ui/form-error";
import { RESETDATA_MASTER } from "@/lib/role-labels";

interface Props {
  deal: Deal;
  /** Channel partner (reseller) name, for the confirm-dialog + toast copy. */
  partnerName?: string | null;
  /** Called after a successful decision (post toast + 1.5s hold). Drawer →
   * close; standalone route → navigate to the dashboard. */
  onDone: () => void;
}

/** Hold the success toast on screen before finishing (CHP-223 — "a 1.5-second
 * hold ensures the message is visible"). */
const REDIRECT_HOLD_MS = 1500;

/** Ops-only "Internal notes" textarea for the decision dialogs.
 * The value is sent as `internal_notes` (staff-only readback on ManagerDeal)
 * and is NEVER rendered on any partner-visible surface. */
function InternalNotesField({
  id,
  value,
  onChange,
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="space-y-1">
      <Label htmlFor={id}>Internal notes (optional)</Label>
      <p className="text-xs text-muted-foreground">
        Visible to ResetData staff only. Never shared with the channel partner.
      </p>
      <Textarea
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        maxLength={2000}
        rows={3}
        placeholder="Visible to ResetData staff only. Never shared with the channel partner."
      />
    </div>
  );
}

export function DealDecisionActions({ deal, partnerName, onDone }: Props) {
  const { accessToken, data: me } = useCurrentUser();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Flow B "Manual Adjudication" → PRIVILEGED OVERRIDES: the systemic
  // force-bypass is restricted to ResetData Masters (role=admin AND
  // is_resetdata_master). CM/RA/non-master admins never see the affordance.
  // Mirrors the backend gate on POST /deals/{id}/force-bypass exactly.
  const isMaster = me?.role === "admin" && me?.is_resetdata_master === true;

  const [confirmApprove, setConfirmApprove] = React.useState(false);
  const [confirmDecline, setConfirmDecline] = React.useState(false);
  const [confirmBypass, setConfirmBypass] = React.useState(false);
  // Ops-only internal notes (deals-gap wave 2). Only one dialog is open at a
  // time, so the notes state is shared and reset on each open.
  const [internalNotes, setInternalNotes] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  // CHP-809: after a successful decision the form resets (dialogs close) while
  // query invalidation/navigation is deferred for the 1.5s hold. Keep the
  // trigger buttons inert through that window so a second click can't fire a
  // duplicate POST against the already-decided deal.
  const [finishing, setFinishing] = React.useState(false);

  const holdTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  React.useEffect(
    () => () => {
      if (holdTimer.current) clearTimeout(holdTimer.current);
    },
    [],
  );

  const partnerLabel = partnerName ?? "the channel partner";

  // Hold on the toast, then refresh the dashboard data and finish (close the
  // drawer / navigate). Invalidation is deferred until the timer fires so these
  // actions stay mounted for the full hold rather than unmounting the moment
  // the deal leaves pending_review.
  const holdThenDone = React.useCallback(() => {
    setFinishing(true);
    holdTimer.current = setTimeout(() => {
      void queryClient.invalidateQueries({ queryKey: ["deals"] });
      onDone();
    }, REDIRECT_HOLD_MS);
  }, [onDone, queryClient]);

  const approve = useMutation({
    mutationFn: () => {
      const notes = internalNotes.trim();
      return approveDeal(accessToken!, deal.id, notes ? { internal_notes: notes } : undefined);
    },
    onSuccess: () => {
      setConfirmApprove(false);
      toast(dealToasts.registrationApproved());
      holdThenDone();
    },
    onError: () => setError("Approve failed. Refresh and try again."),
  });

  const deny = useMutation({
    mutationFn: (reason: string) => {
      const notes = internalNotes.trim();
      return denyDeal(accessToken!, deal.id, reason, notes ? { internal_notes: notes } : undefined);
    },
    onSuccess: () => {
      setConfirmDecline(false);
      toast(dealToasts.registrationDenied());
      holdThenDone();
    },
    onError: () => setError("Denial failed. Refresh and try again."),
  });

  // CHP-809: RM-only systemic force-bypass — instantly override any downstream
  // roadblock (Flow B). Routes through the existing RM-gated client fn against
  // POST /deals/{id}/force-bypass and reflects success/failure in the same
  // toast + error channel as Approve/Deny.
  const forceBypass = useMutation({
    mutationFn: (reason?: string) => forceBypassDeal(accessToken!, deal.id, reason),
    onSuccess: () => {
      setConfirmBypass(false);
      toast(dealToasts.forceBypassApplied());
      holdThenDone();
    },
    onError: () => setError("Force bypass failed. Refresh and try again."),
  });

  const anyPending = approve.isPending || deny.isPending || forceBypass.isPending;
  // Keep the trays inert while a mutation runs AND through the post-success hold.
  const inert = anyPending || finishing;

  if (deal.deal_status !== "pending_review") {
    return null;
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          onClick={() => {
            setInternalNotes("");
            setConfirmApprove(true);
          }}
          disabled={inert}
        >
          Approve registration
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            setInternalNotes("");
            setConfirmDecline(true);
          }}
          disabled={inert}
        >
          Deny registration
        </Button>
        {/* CHP-809: RM-only systemic force-bypass override, co-located with
            Approve/Deny per Flow B. Hidden for CM/RA/non-master admins. */}
        {isMaster ? (
          <Button
            type="button"
            variant="outline"
            onClick={() => setConfirmBypass(true)}
            disabled={inert}
            data-testid="deal-force-bypass"
          >
            Force bypass
          </Button>
        ) : null}
      </div>

      <ConfirmDialog
        open={confirmApprove}
        onOpenChange={setConfirmApprove}
        title={`Approve registration for ${deal.client_name} from ${partnerLabel}?`}
        description="The partner will be notified upon confirmation."
        confirmLabel="Confirm"
        pending={approve.isPending}
        onConfirm={() => {
          setError(null);
          approve.mutate();
        }}
      >
        <InternalNotesField
          id="approve-internal-notes"
          value={internalNotes}
          onChange={setInternalNotes}
        />
      </ConfirmDialog>

      <ConfirmDialog
        open={confirmDecline}
        onOpenChange={setConfirmDecline}
        title="Deny registration?"
        variant="destructive"
        confirmLabel="Confirm denial"
        reason={{
          label: "Denial reason (sent to channel partner)",
          placeholder:
            "Please provide a brief rationale that will be shared with the channel partner.",
          required: true,
          maxLength: 500,
        }}
        pending={deny.isPending}
        onConfirm={(reason) => {
          setError(null);
          deny.mutate(reason ?? "");
        }}
      >
        <InternalNotesField
          id="decline-internal-notes"
          value={internalNotes}
          onChange={setInternalNotes}
        />
      </ConfirmDialog>

      {/* CHP-809: RM-only force-bypass confirm. The reason is optional (the
          backend payload allows null) and is recorded as the approval
          justification — a force-bypass IS an approval. */}
      {isMaster ? (
        <ConfirmDialog
          open={confirmBypass}
          onOpenChange={setConfirmBypass}
          title={`Force bypass account verification for ${deal.client_name}?`}
          description={`This instantly overrides any downstream account-verification roadblock and approves the registration. Restricted to ${RESETDATA_MASTER}s.`}
          variant="destructive"
          confirmLabel="Confirm force bypass"
          reason={{
            label: "Reason (optional, recorded in the audit log)",
            placeholder: "Why is the verification roadblock being overridden?",
            required: false,
            maxLength: 500,
          }}
          pending={forceBypass.isPending}
          onConfirm={(reason) => {
            setError(null);
            forceBypass.mutate(reason && reason.length > 0 ? reason : undefined);
          }}
        />
      ) : null}

      {error ? <FormError>{error}</FormError> : null}
    </div>
  );
}
